import React, { useState, useEffect, useRef, useMemo } from "react";
import { COLORS } from "../../../theme/colors";
import TemplateTile from "./TemplateTile";
import { categoryLabel, DEFAULT_CATEGORIES } from "./iconForTemplate";

// agent-experience-redesign-v1.md §20.4 / §20.6 M3.
//
// Marketplace iPhone-grid. Sectioned by template.category (12
// domain defaults + user-defined). Mirrors AgentTileGrid's
// chunked-render pattern for perf at >50 templates.
//
// Differences from AgentTileGrid:
//   • No status sections (Live/Drafts/Archived) — Marketplace
//     templates don't have a runtime status.
//   • Sections are categories; collapsed-by-default for the
//     "generic" section since it's the fallback bucket.
//   • Templates with no category fall into an "Uncategorized"
//     section that always renders last.
//   • Empty-default-category sections are SKIPPED (a default
//     category with zero templates isn't visible) — keeps the
//     grid scannable. Custom categories always render if they
//     have at least one template.

const CHUNK_SIZE = 60;

// FCOV: pure category bucketing. Buckets templates by `category`, preserving
// incoming order within each bucket, then orders the buckets: default
// categories (that have ≥1 template) in canonical order, custom categories
// alphabetically, "uncategorized" always last. Extracted + exported so the
// ordering rules can be unit-tested directly; wired into the useMemo below.
export function bucketTemplatesByCategory(templates) {
  const byCat = new Map();
  for (const t of (templates || [])) {
    const cat = t.category || "uncategorized";
    if (!byCat.has(cat)) byCat.set(cat, []);
    byCat.get(cat).push(t);
  }
  // Sort: defaults in canonical order, then customs alphabetically,
  // then "uncategorized" last.
  const defaultsPresent = DEFAULT_CATEGORIES.filter(c => byCat.has(c) && byCat.get(c).length > 0);
  const allKeys = [...byCat.keys()];
  const customs = allKeys
    .filter(c => !DEFAULT_CATEGORIES.includes(c) && c !== "uncategorized")
    .sort((a, b) => a.localeCompare(b));
  const ordered = [...defaultsPresent, ...customs];
  if (byCat.has("uncategorized")) ordered.push("uncategorized");
  return ordered.map(cat => ({
    key: cat,
    label: cat === "uncategorized" ? "Uncategorized" : categoryLabel(cat),
    // `cat` always comes from `byCat`'s own keys (defaultsPresent / customs /
    // the uncategorized push are all gated on byCat membership), so
    // byCat.get(cat) is never undefined here.
    items: byCat.get(cat),
    defaultCollapsed: cat === "generic",
  }));
}

export default function MarketplaceTileGrid({
  templates,
  onTileClick,
  onTileInstall,
  onTileContextMenu,
  onTileHover,
  onTileHoverEnd,
  emptyState,
  pinnedCategory,  // when filter chip selected, only render that section
  density = "comfortable",  // i2-visual-identity-v2 §10.1 A8
}) {
  // Bucket templates by category. Preserve incoming order within each
  // bucket (caller already applied sort).
  const buckets = useMemo(() => bucketTemplatesByCategory(templates), [templates]);

  const visible = pinnedCategory ? buckets.filter(b => b.key === pinnedCategory) : buckets;

  if (visible.reduce((s, b) => s + b.items.length, 0) === 0) {
    return emptyState || null;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
      {visible.map(b => (
        <CategorySection
          key={b.key}
          label={b.label}
          items={b.items}
          density={density}
          defaultCollapsed={b.defaultCollapsed && !pinnedCategory}
          onTileClick={onTileClick}
          onTileInstall={onTileInstall}
          onTileContextMenu={onTileContextMenu}
          onTileHover={onTileHover}
          onTileHoverEnd={onTileHoverEnd}
        />
      ))}
    </div>
  );
}

function CategorySection({
  label, items, defaultCollapsed,
  onTileClick, onTileInstall, onTileContextMenu, onTileHover, onTileHoverEnd,
  density,
}) {
  const [collapsed, setCollapsed] = useState(!!defaultCollapsed);
  return (
    <section>
      <div
        onClick={() => setCollapsed(c => !c)}
        style={{
          display: "flex", alignItems: "center", gap: 8, marginBottom: 12,
          cursor: "pointer", userSelect: "none",
        }}
      >
        <div style={{
          fontSize: 12, fontWeight: 700, color: COLORS.text,
          textTransform: "uppercase", letterSpacing: "0.08em",
        }}>{label}</div>
        <div style={{ fontSize: 11, color: COLORS.textDim }}>· {items.length}</div>
        <div style={{ flex: 1, height: 1, background: COLORS.border, opacity: 0.5 }} />
        <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: "monospace" }}>
          {collapsed ? "▸" : "▾"}
        </div>
      </div>
      {!collapsed && (
        <ChunkedGrid
          items={items}
          density={density}
          onTileClick={onTileClick}
          onTileInstall={onTileInstall}
          onTileContextMenu={onTileContextMenu}
          onTileHover={onTileHover}
          onTileHoverEnd={onTileHoverEnd}
        />
      )}
    </section>
  );
}

// Same chunked-render pattern as AgentTileGrid: render CHUNK_SIZE
// initially; IntersectionObserver on a bottom sentinel pulls the
// next chunk in when the user scrolls within ~one viewport of
// the end. Resets to one chunk on items-array identity change so
// filter churn doesn't grow visibleCount unboundedly.
function ChunkedGrid({ items, density, onTileClick, onTileInstall, onTileContextMenu, onTileHover, onTileHoverEnd }) {
  const [visibleCount, setVisibleCount] = useState(() => Math.min(items.length, CHUNK_SIZE));
  const sentinelRef = useRef(null);

  useEffect(() => {
    setVisibleCount(Math.min(items.length, CHUNK_SIZE));
  }, [items]);

  useEffect(() => {
    if (visibleCount >= items.length) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisibleCount(items.length);
      return;
    }
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const obs = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setVisibleCount(prev => Math.min(items.length, prev + CHUNK_SIZE));
        }
      }
    }, { rootMargin: "400px" });
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [visibleCount, items.length]);

  const visibleItems = visibleCount >= items.length ? items : items.slice(0, visibleCount);
  const hasMore = visibleCount < items.length;

  return (
    <>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
        gap: 14,
        alignItems: "stretch",
      }}>
        {visibleItems.map(t => (
          <TemplateTile
            key={t.id}
            template={t}
            density={density}
            onClick={onTileClick}
            onInstall={onTileInstall}
            onContextMenu={onTileContextMenu}
            onHover={onTileHover}
            onHoverEnd={onTileHoverEnd}
          />
        ))}
      </div>
      {hasMore && (
        <div ref={sentinelRef} style={{
          padding: "12px 0", textAlign: "center",
          color: COLORS.textDim, fontSize: 11,
        }}>
          {`Showing ${visibleCount} of ${items.length} — scrolling reveals more`}
        </div>
      )}
    </>
  );
}
